import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { useToast } from '../context/ToastContext';
import { useRealtime } from '../context/RealtimeContext';
import { canManageCommunity, canModerateSession, SESSION_ROLES, ROLES } from '../utils/permissions';
import usePolling from './usePolling';

export const useRoom = (id) => {
  const { user } = useAuth();
  const { rooms } = useActivities();
  const { addToast } = useToast();
  const { subscribe, unsubscribe } = useRealtime();
  
  const room = (Array.isArray(rooms) ? rooms : []).find(a => String(a.id) === String(id) || a.slug === id);
  
  const [roomData, setRoomData] = useState(room);
  const [sessionState, setSessionState] = useState(room?.status?.toLowerCase() || 'live');
  const [sessionRole, setSessionRole] = useState(SESSION_ROLES.PARTICIPANT);
  const [timeLeft, setTimeLeft] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(!room);
  
  useEffect(() => {
    const unlockedRooms = JSON.parse(sessionStorage.getItem('unlocked_rooms') || '[]');
    setIsUnlocked(unlockedRooms.includes(id));
    setIsRestricted(false);
    setShowPasswordPrompt(false);
    setAccessError('');

    // Subscribe to realtime channels
    if (id) {
      subscribe(`room.${id}.typing`);
      subscribe(`room.${id}.reactions`);
    }

    const handleReactionEvent = (e) => {
      const { emoji, x, entity_id, entity_type } = e.detail;
      if (entity_type === 'room' && String(entity_id) === String(id)) {
        showReaction(emoji, x);
      }
    };

    window.addEventListener('room-reaction', handleReactionEvent);

    return () => {
      if (id) {
        unsubscribe(`room.${id}.typing`);
        unsubscribe(`room.${id}.reactions`);
      }
      window.removeEventListener('room-reaction', handleReactionEvent);
    };
  }, [id, subscribe, unsubscribe]);
  
  const reactionTimeoutRef = useRef({});

  const computeTime = useCallback((data) => {
    if (!data) return 0;
    const status = data.status?.toLowerCase();
    if (status === 'upcoming' && data.scheduled_at) {
      return Math.max(0, Math.floor((new Date(data.scheduled_at).getTime() - Date.now()) / 1000));
    }
    if (status === 'live') {
      const startTime = data.scheduled_at ? new Date(data.scheduled_at).getTime() : new Date(data.created_at).getTime();
      return Math.max(0, Math.floor((Date.now() - startTime) / 1000));
    }
    return 0;
  }, []);

  const deriveSessionRole = useCallback((data) => {
    if (!data || !user) return SESSION_ROLES.PARTICIPANT;

    const currentUserId = String(user.id);
    const isHost = [
      data.owner_id,
      data.host_id,
      data.creator_id,
      data.user_id,
      data.author_id,
    ].some((value) => String(value) === currentUserId);

    if (isHost) return SESSION_ROLES.HOST;

    const speakerCandidates = [
      ...(Array.isArray(data.speakers) ? data.speakers : []),
      ...(Array.isArray(data.settings?.speakers) ? data.settings.speakers : []),
    ];

    const normalizedSpeakers = speakerCandidates.map((candidate) => {
      if (candidate && typeof candidate === 'object') {
        return String(candidate.id || candidate.user_id || candidate.name || candidate.label || candidate.value || candidate);
      }
      return String(candidate);
    });

    if (normalizedSpeakers.some((candidate) => candidate === currentUserId || candidate === String(user.name))) {
      return SESSION_ROLES.SPEAKER;
    }

    if (user?.role === ROLES.SESSION_MODERATOR || canManageCommunity(user, data.community_id, data.community)) {
      return SESSION_ROLES.MODERATOR;
    }

    return SESSION_ROLES.PARTICIPANT;
  }, [user]);

  const checkAccess = useCallback((data, unlockedStatus) => {
    if (!data) return;
    const settings = data.settings || {};
    const privacy = data.privacy || settings.privacy || 'public';
    const isInviteOnly = settings.invite_only || false;
    
    // Always grant access to owner, admins, or moderators
    if (user && (
      String(data.owner_id) === String(user.id) || 
      user.role === 'platform_admin' ||
      canManageCommunity(user, data.community_id, data.community)
    )) {
      setIsUnlocked(true);
      setIsRestricted(false);
      setShowPasswordPrompt(false);
      return;
    }

    // Check if user is a speaker
    const role = deriveSessionRole(data);
    if (role === SESSION_ROLES.SPEAKER) {
      setIsUnlocked(true);
      setIsRestricted(false);
      setShowPasswordPrompt(false);
      return;
    }

    if (privacy === 'public') {
      setIsUnlocked(true);
      setIsRestricted(false);
      return;
    }

    // Handle Private / Invite Only
    if (isInviteOnly && role === SESSION_ROLES.PARTICIPANT) {
      // If invite only and not invited, check if they have a password to bypass
      if (data.has_password || settings.password) {
        if (!unlockedStatus) {
          setShowPasswordPrompt(true);
          setIsRestricted(false);
        } else {
          setIsUnlocked(true);
          setIsRestricted(false);
        }
      } else {
        // No password and not invited = restricted
        setIsRestricted(true);
        setIsUnlocked(false);
        setShowPasswordPrompt(false);
      }
      return;
    }

    if (data.has_password || settings.password) {
      if (!unlockedStatus) {
        setShowPasswordPrompt(true);
        setIsRestricted(false);
      } else {
        setIsUnlocked(true);
        setIsRestricted(false);
      }
    } else {
      setIsUnlocked(true);
      setIsRestricted(false);
    }
  }, [user, deriveSessionRole]);

  const reloadRoom = useCallback(async () => {
    if (!id) return;
    try {
      const latestRoom = await api.get(`/rooms/${id}`);
      setRoomData(latestRoom);
      setSessionRole(deriveSessionRole(latestRoom));
      setSessionState(latestRoom.status?.toLowerCase() || 'live');
      
      const unlockedRooms = JSON.parse(sessionStorage.getItem('unlocked_rooms') || '[]');
      checkAccess(latestRoom, unlockedRooms.includes(id));
      
      const newTime = computeTime(latestRoom);
      if (Math.abs(newTime - timeLeft) > 10) {
        setTimeLeft(newTime);
      }
      setIsInitialLoading(false);
    } catch (err) {
      console.warn('Failed to refresh room', err);
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        setShowPasswordPrompt(true);
        setIsInitialLoading(false);
      }
    }
  }, [id, checkAccess, computeTime, timeLeft, deriveSessionRole]);

  usePolling(reloadRoom, 5000, [id]);

  useEffect(() => {
    if (room) {
      setRoomData(room);
      setSessionRole(deriveSessionRole(room));
      setSessionState(room.status?.toLowerCase() || 'live');
      setTimeLeft(computeTime(room));
      
      const unlockedRooms = JSON.parse(sessionStorage.getItem('unlocked_rooms') || '[]');
      checkAccess(room, unlockedRooms.includes(id));
      setIsInitialLoading(false);
    }
  }, [room, id, checkAccess, computeTime, deriveSessionRole]);

  useEffect(() => {
    if (sessionState !== 'live' && sessionState !== 'upcoming') return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (sessionState === 'upcoming') return prev > 0 ? prev - 1 : 0;
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionState]);

  useEffect(() => {
    return () => {
      Object.values(reactionTimeoutRef.current).forEach(clearTimeout);
    };
  }, []);

  const handleUnlock = () => {
    setIsUnlocked(true);
    setShowPasswordPrompt(false);
    const unlockedRooms = JSON.parse(sessionStorage.getItem('unlocked_rooms') || '[]');
    if (!unlockedRooms.includes(id)) {
      unlockedRooms.push(id);
      sessionStorage.setItem('unlocked_rooms', JSON.stringify(unlockedRooms));
    }
  };

  const handlePasswordSubmit = async (password) => {
    try {
      await api.post(`/rooms/${id}/unlock`, { password });
      handleUnlock();
      setAccessError('');
      return true;
    } catch (err) {
      setAccessError(err?.response?.data?.message || 'Incorrect password. Please try again.');
      return false;
    }
  };

  const updateSessionStatus = async (newStatus) => {
    try {
      const statusMap = {
        live: 'Live',
        paused: 'Paused',
        locked: 'Locked',
        archived: 'Archived',
        upcoming: 'Upcoming'
      };
      const formattedStatus = statusMap[newStatus] || newStatus;
      await api.put(`/rooms/${id}`, { status: formattedStatus });
      setSessionState(newStatus);
      addToast(`Session status updated to ${formattedStatus}`, 'success');
    } catch (err) {
      console.error('Failed to update session status', err);
      addToast('Failed to update status', 'error');
    }
  };

  const showReaction = (emoji, x) => {
    const rid = Date.now() + Math.random();
    setReactions(prev => {
      const newReactions = [...prev, { id: rid, emoji, x }];
      return newReactions.slice(-20);
    });
    reactionTimeoutRef.current[rid] = setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== rid));
      delete reactionTimeoutRef.current[rid];
    }, 2000);
  };

  const handleReaction = async (emoji) => {
    const x = Math.random() * 80 + 10;
    // Local show
    showReaction(emoji, x);
    
    // Broadcast via API
    try {
      await api.post(`/rooms/${id}/react`, { emoji, x });
    } catch (err) {
      console.warn('Failed to broadcast reaction', err);
    }
  };

  const handleInvite = () => {
    navigator.clipboard.writeText(window.location.href);
    addToast('Invite link copied to clipboard!', 'share');
  };

  const isModerator = canModerateSession(user, roomData?.id, roomData?.community_id, roomData) || sessionRole === SESSION_ROLES.HOST || sessionRole === SESSION_ROLES.MODERATOR;

  return {
    roomData,
    sessionState,
    sessionRole,
    setSessionRole,
    timeLeft,
    reactions,
    isUnlocked,
    isRestricted,
    showPasswordPrompt,
    accessError,
    isInitialLoading,
    isModerator,
    handlePasswordSubmit,
    updateSessionStatus,
    handleReaction,
    handleInvite,
    reloadRoom,
    activeParticipants: roomData?.active_participants || []
  };
};
